import { TenantAccessError, type CreateRequestInput, type RequestListInput, type RequestRepository, type TenantContext } from '@zetyar/data-contracts'
import type { ServiceRequest, ServiceRequestComment, ServiceRequestStatus, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface RequestRow {
  readonly id: UUID
  readonly subject: string
  readonly description: string
  readonly category: string | null
  readonly status: string
  readonly created_at: string
  readonly updated_at: string
  readonly closed_at: string | null
  readonly requested_by: { readonly id: UUID; readonly display_name: string } | null
  readonly assigned_to: { readonly id: UUID; readonly display_name: string } | null
}

function mapRequest(row: RequestRow, organizationId: UUID): ServiceRequest {
  return {
    id: row.id,
    organizationId,
    requestedById: row.requested_by?.id ?? '',
    subject: row.subject,
    description: row.description,
    category: row.category,
    status: row.status as ServiceRequestStatus,
    assignedToId: row.assigned_to?.id ?? null,
    closedAt: row.closed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    requestedByDisplayName: row.requested_by?.display_name,
    assignedToDisplayName: row.assigned_to?.display_name ?? null,
  }
}

const REQUEST_SELECT = `
  id, subject, description, category, status, created_at, updated_at, closed_at,
  requested_by:memberships!service_requests_requested_by_membership_id_fkey (id, display_name),
  assigned_to:memberships!service_requests_assigned_to_membership_id_fkey (id, display_name)
`

export function createSupabaseRequestRepository(client: SupabaseClient): RequestRepository {
  return {
    async listMine(context: TenantContext, { limit }: RequestListInput) {
      if (!context.membershipId) return []

      const { data, error } = await client
        .from('service_requests')
        .select(REQUEST_SELECT)
        .eq('organization_id', context.organizationId)
        .eq('requested_by_membership_id', context.membershipId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error
      return (data as unknown as RequestRow[]).map((row) => mapRequest(row, context.organizationId))
    },

    async listForOrganization(context: TenantContext, { limit }: RequestListInput) {
      const { data, error } = await client
        .from('service_requests')
        .select(REQUEST_SELECT)
        .eq('organization_id', context.organizationId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error
      return (data as unknown as RequestRow[]).map((row) => mapRequest(row, context.organizationId))
    },

    async get(context: TenantContext, requestId: UUID, commentLimit: number) {
      const { data: requestRow, error: requestError } = await client
        .from('service_requests')
        .select(REQUEST_SELECT)
        .eq('id', requestId)
        .eq('organization_id', context.organizationId)
        .maybeSingle()

      if (requestError) throw requestError
      if (!requestRow) return null

      const { data: commentRows, error: commentsError } = await client
        .from('service_request_comments')
        .select('id, content, created_at, author:memberships!service_request_comments_author_membership_id_fkey (id, display_name)')
        .eq('request_id', requestId)
        .order('created_at', { ascending: true })
        .limit(Math.max(1, Math.min(commentLimit, 200)))

      if (commentsError) throw commentsError

      const comments: ServiceRequestComment[] = (commentRows as unknown as {
        id: UUID; content: string; created_at: string; author: { id: UUID; display_name: string } | null
      }[]).map((row) => ({
        id: row.id,
        requestId,
        authorId: row.author?.id ?? '',
        content: row.content,
        createdAt: row.created_at,
        authorDisplayName: row.author?.display_name,
      }))

      return { request: mapRequest(requestRow as unknown as RequestRow, context.organizationId), comments }
    },

    async create(context: TenantContext, input: CreateRequestInput) {
      if (!context.membershipId) throw new TenantAccessError('Membership is required to create a request')

      const { error } = await client.from('service_requests').insert({
        organization_id: context.organizationId,
        requested_by_membership_id: context.membershipId,
        subject: input.subject,
        description: input.description,
        category: input.category ?? null,
      })
      if (error) throw error
    },

    async addComment(context: TenantContext, requestId: UUID, requestedById: UUID, content: string) {
      if (!context.membershipId) throw new TenantAccessError('Membership is required to comment on a request')

      const { error } = await client.from('service_request_comments').insert({
        request_id: requestId,
        author_membership_id: context.membershipId,
        content,
      })
      if (error) throw error
      void requestedById
    },

    async updateStatus(context: TenantContext, requestId: UUID, notifyMembershipId: UUID, status: ServiceRequestStatus) {
      const { error } = await client.rpc('update_request_status', {
        p_id: requestId,
        p_organization_id: context.organizationId,
        p_status: status,
      })
      if (error) throw error
      void notifyMembershipId
    },

    async assign(context: TenantContext, requestId: UUID, assignedToMembershipId: UUID) {
      const { error } = await client.rpc('assign_request', {
        p_id: requestId,
        p_organization_id: context.organizationId,
        p_assigned_to_membership_id: assignedToMembershipId,
      })
      if (error) throw error
    },
  }
}
